// client/src/utils/consoleFilters.js

/**
 * Console filtering utility to suppress console output in production builds
 * and filter noisy third-party library logs in development.
 *
 * PRODUCTION MODE: All console methods (except errors) are suppressed completely.
 * DEVELOPMENT MODE: Only GSI_LOGGER messages are filtered when enabled.
 */

/**
 * Initializes console filtering based on environment.
 * This should be called early in the application lifecycle, before any
 * third-party scripts (like Google Identity Services) are loaded.
 */
export function initConsoleFilters() {
  const isProduction = process.env.NODE_ENV === 'production'
  const suppressGSI = process.env.REACT_APP_SUPPRESS_GSI_LOGGER === 'true'

  // In production, suppress all console output except errors
  if (isProduction) {
    suppressProductionConsole()
    return
  }

  // In development, only apply GSI filtering if enabled
  if (!suppressGSI) {
    // No suppression enabled, leave console methods unchanged
    return
  }

  // Apply GSI filtering for development
  suppressGSILoggerMessages()
}

/**
 * Suppresses all console methods except error in production builds.
 * This prevents debug information and sensitive data from being exposed
 * in the browser console for end users.
 */
function suppressProductionConsole() {
  // Store original console.error for critical issues
  const originalError = console.error

  // Replace all console methods with no-op functions
  console.log = () => {}
  console.warn = () => {}
  console.info = () => {}
  console.debug = () => {}

  // Keep console.error active but can be extended to send to error tracking service
  console.error = function (...args) {
    // Still call original error for critical issues
    originalError.apply(console, args)

    // TODO: Integrate with error tracking service (e.g., Sentry)
    // sendToErrorTracking('error', args)
  }
}

/**
 * Suppresses GSI_LOGGER messages in development mode.
 * Legacy function maintained for backward compatibility.
 */
function suppressGSILoggerMessages() {
  // Store original console methods
  const originalConsole = {
    log: console.log,
    warn: console.warn,
    info: console.info,
    debug: console.debug,
    error: console.error,
  }

  /**
   * Creates a filtered version of a console method that suppresses
   * messages matching GSI patterns.
   */
  const createFilteredConsoleMethod = (originalMethod) => {
    return function (...args) {
      // Check if any of the arguments contain GSI_LOGGER
      const hasGSILogger = args.some((arg) => {
        if (typeof arg === 'string') {
          return arg.includes('GSI_LOGGER')
        }
        return false
      })

      // If it's a GSI_LOGGER message, suppress it
      if (hasGSILogger) {
        return
      }

      // Otherwise, pass through to original console method
      originalMethod.apply(console, args)
    }
  }

  // Replace console methods with filtered versions
  console.log = createFilteredConsoleMethod(originalConsole.log)
  console.warn = createFilteredConsoleMethod(originalConsole.warn)
  console.info = createFilteredConsoleMethod(originalConsole.info)
  console.debug = createFilteredConsoleMethod(originalConsole.debug)

  // Keep console.error unchanged for safety - we don't want to suppress actual errors
  // even if they come from GSI, as they might be important
}

/**
 * Restores original console methods (mainly for testing purposes)
 */
export function restoreConsole() {
  // This is a simple implementation - in a real scenario we'd need to
  // store the original methods more carefully, but for our use case
  // this should be sufficient since we initialize early and don't restore
}
